import { clsx } from 'clsx'

import { ChannelIcon } from '@buffer-mono/popcorn'

import { AnimatedTile } from './AnimatedTile'

import styles from './AnimatedBackground.module.css'

import type { JSX } from 'react'

const EMOJI_COLORS = {
  coral: 'hsl(7 100% 83%)',
  purple: 'hsl(258 100% 88%)',
  yellow: 'hsl(40 100% 77%)',
  aqua: 'hsl(174 64% 77%)',
  green: 'hsl(105 68% 77%)',
  fuscia: 'hsl(289 100% 87%)',
  blue: 'hsl(207 100% 84%)',
} as const

function EmojiTileContent({
  emoji,
  color,
  className,
}: {
  emoji: string
  color: keyof typeof EMOJI_COLORS
  className?: string
}): JSX.Element {
  return (
    <span
      className={clsx(styles.emojiTile, className)}
      style={{ backgroundColor: EMOJI_COLORS[color] }}
    >
      {emoji}
    </span>
  )
}

function ChannelTileContent({
  channel,
}: {
  channel: Parameters<typeof ChannelIcon>[0]['type']
}): JSX.Element {
  return (
    <div className={styles.channelTile}>
      <ChannelIcon
        className={styles.channelTileLogo}
        type={channel}
        color="branded"
      />
    </div>
  )
}

export function AnimatedBackground(): JSX.Element {
  return (
    <div className={styles.decoration} aria-hidden="true">
      {/* Emoji tiles layer */}
      <div className={styles.gridContainer}>
        {/* Leading (left) emoji grid */}
        <div
          className={clsx(
            styles.grid,
            styles.gridLeading,
            styles.gridEmojiLeading,
          )}
        >
          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiClap)}
          >
            <EmojiTileContent emoji="👏" color="coral" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiRocket)}
          >
            <EmojiTileContent emoji="🚀" color="purple" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiHeart)}
          >
            <EmojiTileContent emoji="❤️" color="yellow" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiChart)}
          >
            <EmojiTileContent emoji="📈" color="aqua" />
          </AnimatedTile>
        </div>

        {/* Trailing (right) emoji grid */}
        <div className={clsx(styles.grid, styles.gridEmojiTrailing)}>
          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiMegaphone)}
          >
            <EmojiTileContent emoji="📣" color="green" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiChat)}
          >
            <EmojiTileContent emoji="💬" color="fuscia" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiSelfie)}
          >
            <EmojiTileContent emoji="🤳" color="coral" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiCalendar)}
          >
            <EmojiTileContent emoji="🗓️" color="blue" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.emojiRaisedHands)}
          >
            <EmojiTileContent emoji="🙌" color="aqua" />
          </AnimatedTile>
        </div>
      </div>

      {/* Channel tiles layer */}
      <div className={styles.gridContainer}>
        {/* Leading (left) channel grid */}
        <div className={clsx(styles.grid, styles.gridLeading)}>
          <AnimatedTile className={clsx(styles.tileContainer, styles.channelX)}>
            <ChannelTileContent channel="twitter" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelLinkedIn)}
          >
            <ChannelTileContent channel="linkedin" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelInstagram)}
          >
            <ChannelTileContent channel="instagram" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelTikTok)}
          >
            <ChannelTileContent channel="tiktok" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelYouTube)}
          >
            <ChannelTileContent channel="youtube" />
          </AnimatedTile>
        </div>

        {/* Trailing (right) channel grid */}
        <div className={clsx(styles.grid)}>
          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelBluesky)}
          >
            <ChannelTileContent channel="bluesky" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelPinterest)}
          >
            <ChannelTileContent channel="pinterest" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelThreads)}
          >
            <ChannelTileContent channel="threads" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelFacebook)}
          >
            <ChannelTileContent channel="facebook" />
          </AnimatedTile>

          <AnimatedTile
            className={clsx(styles.tileContainer, styles.channelMastodon)}
          >
            <ChannelTileContent channel="mastodon" />
          </AnimatedTile>
        </div>
      </div>
    </div>
  )
}
